(() => {
  const MOBILE_TABLET_QUERY = '(max-width: 839px)';
  const DESKTOP_QUERY = '(min-width: 840px)';
  const DESKTOP_PAGE_SCROLL_QUERY = '(min-width: 990px)';
  const VIEWPORT_SELECTOR = '.main-product-block .custom-swiper .product__media-list';
  const DESKTOP_VIEWPORT_SELECTOR =
    '.main-product-block .old-sldier > .w3-content.w3-display-container';
  const IS_SAFARI =
    typeof navigator !== 'undefined' &&
    /Safari/i.test(navigator.userAgent) &&
    !/(Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|OPiOS|FxiOS|Android)/i.test(navigator.userAgent);

  class ProtemoaPdpCarousel {
    constructor(viewport) {
      this.viewport = viewport;
      this.root = viewport.closest('.custom-swiper');
      this.slides = Array.from(viewport.querySelectorAll('.slider__slide'));
      this.scrollbar = this.root?.querySelector('.custom-mobile-scrollbar') || null;
      this.scrollbarThumb = this.scrollbar?.querySelector('.scrollbar-thumb') || null;

      if (!this.root || this.slides.length < 2) return;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMouseDown = this.handleMouseDown.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);
      this.handleSettleEnd = this.handleSettleEnd.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleDragStart = this.handleDragStart.bind(this);

      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      this.viewport.addEventListener('mousedown', this.handleMouseDown);
      this.viewport.addEventListener('click', this.handleClick, true);
      this.viewport.addEventListener('dragstart', this.handleDragStart);
      window.addEventListener('resize', this.handleResize, { passive: true });
      this.updateProgress();
    }

    isCarouselMode() {
      return window.matchMedia(MOBILE_TABLET_QUERY).matches;
    }

    isVisible() {
      return this.viewport.getClientRects().length > 0 && this.viewport.clientWidth > 0;
    }

    handleScroll() {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = requestAnimationFrame(() => this.updateProgress());
    }

    handleResize() {
      this.cancelDrag();
      this.updateProgress();
    }

    handleMouseDown(event) {
      if (!this.isCarouselMode() || !this.isVisible() || event.button !== 0 || this.isDragging) return;

      this.clearSettleState();
      this.isDragging = true;
      this.dragMoved = false;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.viewport.scrollLeft;
      this.dragStartIndex = this.getActiveIndex();
      this.dragStartTime = performance.now();
      this.viewport.classList.add('is-protemoa-dragging');
      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('mouseup', this.handleMouseUp);
      window.addEventListener('blur', this.handleMouseUp);
    }

    handleMouseMove(event) {
      if (!this.isDragging) return;

      const pointerDelta = event.clientX - this.dragStartX;
      if (Math.abs(pointerDelta) < 4 && !this.dragMoved) return;

      event.preventDefault();
      this.dragMoved = true;
      this.viewport.scrollLeft = this.dragStartScrollLeft - pointerDelta;
      this.updateProgress();
    }

    handleMouseUp() {
      if (!this.isDragging) return;

      const moved = this.dragMoved;
      const scrollDistance = this.viewport.scrollLeft - this.dragStartScrollLeft;
      const swipeDuration = Math.max(performance.now() - this.dragStartTime, 1);
      const viewportWidth = this.viewport.clientWidth;
      this.isDragging = false;
      this.dragMoved = false;
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('mouseup', this.handleMouseUp);
      window.removeEventListener('blur', this.handleMouseUp);

      if (!moved) {
        this.clearSettleState();
        return;
      }

      this.suppressNextClick = true;
      clearTimeout(this.clickResetTimer);
      this.clickResetTimer = setTimeout(() => {
        this.suppressNextClick = false;
      }, 250);

      const distanceThreshold = Math.min(22, Math.max(12, viewportWidth * 0.04));
      const swipeVelocity = Math.abs(scrollDistance) / swipeDuration;
      const intentionalSwipe =
        Math.abs(scrollDistance) >= distanceThreshold ||
        (Math.abs(scrollDistance) >= 8 && swipeVelocity >= 0.12);
      const direction = scrollDistance > 0 ? 1 : -1;
      const targetIndex = intentionalSwipe
        ? Math.max(0, Math.min(this.slides.length - 1, this.dragStartIndex + direction))
        : this.dragStartIndex;

      this.viewport.addEventListener('scrollend', this.handleSettleEnd, { once: true });
      this.settleTimer = setTimeout(this.handleSettleEnd, 650);
      this.viewport.scrollTo({ left: this.getSlidePosition(targetIndex), behavior: 'smooth' });
    }

    handleSettleEnd() {
      this.clearSettleState();
      this.updateProgress();
    }

    clearSettleState() {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
      this.viewport.removeEventListener('scrollend', this.handleSettleEnd);
      this.viewport.classList.remove('is-protemoa-dragging');
    }

    cancelDrag() {
      this.isDragging = false;
      this.dragMoved = false;
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('mouseup', this.handleMouseUp);
      window.removeEventListener('blur', this.handleMouseUp);
      this.clearSettleState();
    }

    handleClick(event) {
      if (!this.suppressNextClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressNextClick = false;
    }

    handleDragStart(event) {
      if (this.isCarouselMode()) event.preventDefault();
    }

    getSlidePosition(index) {
      const firstOffset = this.slides[0]?.offsetLeft || 0;
      const targetOffset = (this.slides[index]?.offsetLeft || firstOffset) - firstOffset;
      const maxScroll = Math.max(this.viewport.scrollWidth - this.viewport.clientWidth, 0);
      return Math.max(0, Math.min(maxScroll, targetOffset));
    }

    getActiveIndex() {
      const scrollLeft = this.viewport.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(this.getSlidePosition(index) - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    updateProgress() {
      if (!this.scrollbar || !this.scrollbarThumb) return;

      if (!this.isCarouselMode()) {
        this.scrollbar.style.removeProperty('display');
        return;
      }

      this.scrollbar.style.setProperty('display', 'block', 'important');
      this.scrollbarThumb.style.setProperty('width', `${100 / this.slides.length}%`, 'important');
      const maxScroll = Math.max(this.viewport.scrollWidth - this.viewport.clientWidth, 0);
      const progress = maxScroll
        ? Math.max(0, Math.min(1, this.viewport.scrollLeft / maxScroll))
        : 0;
      const translate = progress * (this.slides.length - 1) * 100;
      this.scrollbarThumb.style.setProperty('transform', `translate3d(${translate}%, 0, 0)`, 'important');
    }
  }

  class ProtemoaDesktopPdpGallery {
    constructor(viewport) {
      this.viewport = viewport;
      this.root = viewport.closest('.old-sldier');
      this.slides = Array.from(viewport.children).filter((slide) =>
        slide.matches('.mySlides, .mySlides1')
      );
      this.isSafari = IS_SAFARI;

      if (this.root && this.isSafari) {
        this.root.classList.add('is-protemoa-safari-gallery');
      }

      if (!this.root || this.slides.length < 2) return;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleBoundaryWheel = this.handleBoundaryWheel.bind(this);
      this.handleWheel = this.handleWheel.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMouseDown = this.handleMouseDown.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleDragStart = this.handleDragStart.bind(this);

      this.syncGalleryAspectRatio();
      this.fitSafariImages();
      this.viewport.scrollTop = 0;
      this.targetIndex = 0;
      this.buildNavigation();
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      document.addEventListener('wheel', this.handleBoundaryWheel, {
        capture: true,
        passive: true,
      });
      /*
       * Safari owns its nested scroll viewport natively. WebKit does not
       * expose trackpad phase or momentum phase to wheel handlers, so mixing
       * preventDefault() and programmatic smooth scrolling with native scroll
       * snap makes one physical gesture intermittently stall or fight the
       * snap animation. Chromium keeps the existing one-step wheel path.
       */
      if (!this.isSafari) {
        document.addEventListener('wheel', this.handleWheel, {
          capture: true,
          passive: false,
        });
      }
      this.viewport.addEventListener('mousedown', this.handleMouseDown);
      this.viewport.addEventListener('click', this.handleClick, true);
      this.viewport.addEventListener('dragstart', this.handleDragStart);
      window.addEventListener('resize', this.handleResize, { passive: true });
      this.updateActiveSlide();
    }

    syncGalleryAspectRatio() {
      const sourceImage = this.slides[0]?.querySelector('img');
      if (!sourceImage) return;

      const width = Number(sourceImage.getAttribute('width')) || sourceImage.naturalWidth;
      const height = Number(sourceImage.getAttribute('height')) || sourceImage.naturalHeight;
      if (width > 0 && height > 0) {
        this.root.style.setProperty('--protemoa-gallery-aspect-ratio', String(width / height));
        return;
      }

      sourceImage.addEventListener('load', () => this.syncGalleryAspectRatio(), { once: true });
    }

    fitSafariImages() {
      if (!this.isSafari) return;

      /*
       * Safari 18 can resolve the viewport's percentage height from its
       * intrinsic image content instead of the capped outer gallery. Measure
       * the actual outer frame and give the native scroll viewport a pixel
       * height so neither the viewport nor the images can stretch it again.
       */
      const frameWidth = this.root.clientWidth;
      const frameHeight = this.root.clientHeight;
      if (!frameWidth || !frameHeight) return;

      this.viewport.style.setProperty(
        '--protemoa-safari-frame-height',
        `${frameHeight}px`
      );

      const imageFrameHeight = Math.max(frameHeight - 2, 0);

      this.slides.forEach((slide) => {
        const image = slide.querySelector('img');
        if (!image) return;

        const sourceWidth = Number(image.getAttribute('width')) || image.naturalWidth;
        const sourceHeight = Number(image.getAttribute('height')) || image.naturalHeight;
        if (!sourceWidth || !sourceHeight) {
          image.addEventListener('load', () => this.fitSafariImages(), { once: true });
          return;
        }

        const scale = Math.min(frameWidth / sourceWidth, imageFrameHeight / sourceHeight);
        image.style.setProperty('--protemoa-safari-image-width', `${sourceWidth * scale}px`);
        image.style.setProperty('--protemoa-safari-image-height', `${sourceHeight * scale}px`);
      });
    }

    isDesktopMode() {
      return window.matchMedia(DESKTOP_QUERY).matches;
    }

    isVisible() {
      return this.viewport.getClientRects().length > 0 && this.viewport.clientHeight > 0;
    }

    buildNavigation() {
      this.navigation = document.createElement('div');
      this.navigation.className = 'protemoa-desktop-gallery-nav';
      this.navigation.setAttribute('role', 'navigation');
      this.navigation.setAttribute('aria-label', 'Product images');

      this.navigationButtons = this.slides.map((slide, index) => {
        const button = document.createElement('button');
        const sourceImage = slide.querySelector('img');
        button.type = 'button';
        button.className = 'protemoa-desktop-gallery-nav__button';
        button.setAttribute('aria-label', `Show image ${index + 1} of ${this.slides.length}`);
        button.addEventListener('mousedown', (event) => event.preventDefault());
        button.addEventListener('click', () => this.scrollToIndex(index));

        if (sourceImage) {
          const thumbnail = document.createElement('img');
          const source = sourceImage.currentSrc || sourceImage.getAttribute('src') || '';
          thumbnail.src = source.replace(/([?&])width=\d+/i, '$1width=96');
          thumbnail.alt = '';
          thumbnail.loading = 'lazy';
          thumbnail.setAttribute('aria-hidden', 'true');
          button.appendChild(thumbnail);
        }

        this.navigation.appendChild(button);
        return button;
      });

      this.root.appendChild(this.navigation);
    }

    handleScroll() {
      if (!this.isDesktopMode()) return;
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = requestAnimationFrame(() => {
        this.updateActiveSlide();
      });
    }

    handleBoundaryWheel(event) {
      if (!window.matchMedia(DESKTOP_PAGE_SCROLL_QUERY).matches || !this.isVisible()) {
        this.viewport.classList.remove('is-protemoa-desktop-gallery-at-end');
        return;
      }

      const bounds = this.viewport.getBoundingClientRect();
      const isOverGallery =
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom;
      const isVerticalGesture = Math.abs(event.deltaY) > Math.abs(event.deltaX);
      if (!isOverGallery || !isVerticalGesture || !event.deltaY) return;

      const maxScroll = Math.max(this.viewport.scrollHeight - this.viewport.clientHeight, 0);
      const isAtEnd = maxScroll - this.viewport.scrollTop <= 1;
      this.viewport.classList.toggle(
        'is-protemoa-desktop-gallery-at-end',
        event.deltaY > 0 && isAtEnd
      );
    }

    handleWheel(event) {
      if (!this.isDesktopMode() || !this.isVisible()) return;

      const bounds = this.viewport.getBoundingClientRect();
      const isOverGallery =
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom;
      const isVerticalGesture = Math.abs(event.deltaY) > Math.abs(event.deltaX);
      if (!isOverGallery || !isVerticalGesture || !event.deltaY) return;

      /*
       * Chromium marks only the first event in a wheel transaction as
       * cancelable when its default action is left intact. Do not call
       * preventDefault here: hidden native overflow and overscroll containment
       * absorb that action while this marker keeps each gesture to one slide.
       */
      if (!event.cancelable) return;

      const direction = Math.sign(event.deltaY);
      const currentIndex = Number.isInteger(this.targetIndex)
        ? this.targetIndex
        : this.getActiveIndex();
      const nextIndex = Math.max(
        0,
        Math.min(this.slides.length - 1, currentIndex + direction)
      );
      if (nextIndex !== currentIndex) this.scrollToIndex(nextIndex);
    }

    handleResize() {
      this.fitSafariImages();
      this.cancelDrag();
      this.updateActiveSlide();
    }

    handleMouseDown(event) {
      if (!this.isDesktopMode() || !this.isVisible() || event.button !== 0 || this.isDragging) return;

      const image = event.target.closest('.mySlides img, .mySlides1 img');
      if (!image || !this.viewport.contains(image)) return;

      this.isDragging = true;
      this.dragMoved = false;
      this.dragStartY = event.clientY;
      this.dragStartScrollTop = this.viewport.scrollTop;
      this.dragStartIndex = this.getActiveIndex();
      this.dragDeltaY = 0;
      this.viewport.classList.add('is-protemoa-desktop-dragging');
      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('mouseup', this.handleMouseUp);
      window.addEventListener('blur', this.handleMouseUp);
    }

    handleMouseMove(event) {
      if (!this.isDragging) return;

      const pointerDelta = event.clientY - this.dragStartY;
      if (Math.abs(pointerDelta) < 4 && !this.dragMoved) return;

      event.preventDefault();
      this.dragMoved = true;
      this.dragDeltaY = pointerDelta;
      const previousPosition = this.getSlidePosition(Math.max(0, this.dragStartIndex - 1));
      const nextPosition = this.getSlidePosition(
        Math.min(this.slides.length - 1, this.dragStartIndex + 1)
      );
      this.viewport.scrollTop = Math.max(
        previousPosition,
        Math.min(nextPosition, this.dragStartScrollTop - pointerDelta)
      );
      this.updateActiveSlide();
    }

    handleMouseUp() {
      if (!this.isDragging) return;

      const moved = this.dragMoved;
      const dragDirection = Math.sign(-this.dragDeltaY);
      const startIndex = this.dragStartIndex;
      this.isDragging = false;
      this.dragMoved = false;
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('mouseup', this.handleMouseUp);
      window.removeEventListener('blur', this.handleMouseUp);
      this.viewport.classList.remove('is-protemoa-desktop-dragging');

      if (!moved) return;

      this.suppressNextClick = true;
      clearTimeout(this.clickResetTimer);
      this.clickResetTimer = setTimeout(() => {
        this.suppressNextClick = false;
      }, 250);
      const targetIndex = Math.max(
        0,
        Math.min(this.slides.length - 1, startIndex + dragDirection)
      );
      this.scrollToIndex(targetIndex);
    }

    handleClick(event) {
      if (!this.suppressNextClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressNextClick = false;
    }

    handleDragStart(event) {
      if (this.isDesktopMode()) event.preventDefault();
    }

    cancelDrag() {
      this.isDragging = false;
      this.dragMoved = false;
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('mouseup', this.handleMouseUp);
      window.removeEventListener('blur', this.handleMouseUp);
      this.viewport.classList.remove('is-protemoa-desktop-dragging');
    }

    getSlidePosition(index) {
      const firstOffset = this.slides[0]?.offsetTop || 0;
      const targetOffset = (this.slides[index]?.offsetTop || firstOffset) - firstOffset;
      const maxScroll = Math.max(this.viewport.scrollHeight - this.viewport.clientHeight, 0);
      return Math.max(0, Math.min(maxScroll, targetOffset));
    }

    getActiveIndex() {
      const scrollTop = this.viewport.scrollTop;
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(this.getSlidePosition(index) - scrollTop);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    scrollToIndex(index) {
      if (!this.isDesktopMode()) return;
      this.targetIndex = index;
      this.viewport.scrollTo({ top: this.getSlidePosition(index), behavior: 'smooth' });
    }

    updateActiveSlide() {
      if (!this.isDesktopMode()) {
        this.viewport.classList.remove('is-protemoa-desktop-gallery-at-end');
        return;
      }

      const activeIndex = this.getActiveIndex();
      const maxScroll = Math.max(this.viewport.scrollHeight - this.viewport.clientHeight, 0);
      const isAtEnd = maxScroll - this.viewport.scrollTop <= 1;
      if (!window.matchMedia(DESKTOP_PAGE_SCROLL_QUERY).matches || !isAtEnd) {
        this.viewport.classList.remove('is-protemoa-desktop-gallery-at-end');
      }
      if (!this.navigationButtons?.length) return;

      this.navigationButtons.forEach((button, index) => {
        const isActive = index === activeIndex;
        button.classList.toggle('is-active', isActive);
        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
      this.updateNavigationWindow(activeIndex);
    }

    updateNavigationWindow(activeIndex) {
      const firstButton = this.navigationButtons[0];
      const secondButton = this.navigationButtons[1];
      if (!firstButton || !this.navigation.clientHeight) return;

      const measuredStride = secondButton
        ? secondButton.offsetTop - firstButton.offsetTop
        : firstButton.offsetHeight;
      const buttonStride = Math.max(measuredStride, firstButton.offsetHeight, 1);
      const buttonGap = Math.max(buttonStride - firstButton.offsetHeight, 0);
      const visibleCount = Math.max(
        1,
        Math.floor((this.navigation.clientHeight + buttonGap) / buttonStride)
      );
      const maxStartIndex = Math.max(this.navigationButtons.length - visibleCount, 0);
      const desiredStartIndex = Math.max(
        0,
        Math.min(activeIndex - visibleCount + 2, maxStartIndex)
      );
      const desiredScrollTop = this.navigationButtons[desiredStartIndex]?.offsetTop || 0;

      if (desiredScrollTop !== this.navigation.scrollTop) {
        this.navigation.scrollTo({ top: desiredScrollTop, behavior: 'smooth' });
      }
    }
  }

  const syncDesktopGalleryRoot = (viewport) => {
    const root = viewport.closest('.old-sldier');
    if (!root) return;

    const sync = () => {
      root.classList.toggle(
        'is-protemoa-desktop-gallery-hidden',
        window.getComputedStyle(viewport).display === 'none'
      );
    };

    sync();
    if (viewport.dataset.protemoaDesktopVisibilityReady === 'true') return;

    viewport.dataset.protemoaDesktopVisibilityReady = 'true';
    new MutationObserver(sync).observe(viewport, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll(VIEWPORT_SELECTOR).forEach((viewport) => {
      if (viewport.dataset.protemoaCarouselReady === 'true') return;
      viewport.dataset.protemoaCarouselReady = 'true';
      new ProtemoaPdpCarousel(viewport);
    });

    if (window.matchMedia(DESKTOP_QUERY).matches) {
      scope.querySelectorAll(DESKTOP_VIEWPORT_SELECTOR).forEach((viewport) => {
        syncDesktopGalleryRoot(viewport);
        if (viewport.dataset.protemoaDesktopGalleryReady === 'true') return;
        viewport.dataset.protemoaDesktopGalleryReady = 'true';
        new ProtemoaDesktopPdpGallery(viewport);
      });
    }
  };

  initialize();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('resize', () => initialize(), { passive: true });
})();
